import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';
import { useTenant } from '../../context/TenantContext';
import { toast } from 'sonner';

const Integrations = () => {
  const { activeTenant } = useTenant();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (!activeTenant) return;
    setLoading(true);
    api.get(`/integrations?tenant_id=${activeTenant}`)
      .then((res) => setIntegrations(res.data || []))
      .catch(() => toast.error('Error al cargar integraciones'))
      .finally(() => setLoading(false));
  }, [activeTenant]);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return integrations.filter((intg) => {
      const matchesQuery =
        !normalized ||
        intg.name?.toLowerCase().includes(normalized) ||
        intg.type?.toLowerCase().includes(normalized);
      const matchesStatus = statusFilter === 'all' ? true : intg.status === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [integrations, query, statusFilter]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      const aValue = (a[sortKey] || '').toString().toLowerCase();
      const bValue = (b[sortKey] || '').toString().toLowerCase();
      if (aValue < bValue) return -1 * dir;
      if (aValue > bValue) return 1 * dir;
      return 0;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const allSelected = sorted.length > 0 && sorted.every((i) => selected[i.id]);
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected({});
      return;
    }
    const next = {};
    sorted.forEach((intg) => { next[intg.id] = true; });
    setSelected(next);
  };
  const toggleRow = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleBulkCopy = () => {
    const names = sorted.filter((i) => selected[i.id]).map((i) => i.name);
    if (!names.length) {
      toast.error('Selecciona al menos una integracion');
      return;
    }
    navigator.clipboard?.writeText(names.join('\n'));
    toast.success('Nombres copiados');
  };

  return (
    <div data-testid="integrations-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-4xl font-heading font-bold">Integraciones</h1>
          <p className="text-muted-foreground">Estado de conexiones y servicios</p>
        </div>
        <Button variant="outline" onClick={handleBulkCopy}>Copiar nombres</Button>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Input
            placeholder="Buscar por nombre o tipo"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-xs"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="all">Todos</option>
            <option value="active">Activo</option>
            <option value="inactive">Inactivo</option>
            <option value="error">Error</option>
          </select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                </th>
                <th className="px-6 py-3 text-left cursor-pointer" onClick={() => toggleSort('name')}>Nombre</th>
                <th className="px-6 py-3 text-left cursor-pointer" onClick={() => toggleSort('type')}>Tipo</th>
                <th className="px-6 py-3 text-left cursor-pointer" onClick={() => toggleSort('status')}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" className="px-6 py-4 text-center">Cargando...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan="4" className="px-6 py-8 text-center text-muted-foreground">No hay integraciones</td></tr>
              ) : (
                sorted.map((intg) => (
                  <tr key={intg.id} className="border-t">
                    <td className="px-4 py-4">
                      <input type="checkbox" checked={Boolean(selected[intg.id])} onChange={() => toggleRow(intg.id)} />
                    </td>
                    <td className="px-6 py-4">{intg.name}</td>
                    <td className="px-6 py-4">{intg.type}</td>
                    <td className="px-6 py-4">{intg.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};
export default Integrations;
